import { noop } from "lodash";
import { useTranslation } from "next-i18next";
import { ChangeEvent, FC, SyntheticEvent } from "react";
import { Form } from "react-final-form";
import { useRouter } from "next/router";
import FormTextField from "@Components/forms/formFields/textField/FormTextField";
import { FormMessage } from "@Components/forms/formMessage/FormMessage";
import CTFieldValidator, {
  email,
  required,
  strongPassword,
  strongPasswordForCA,
} from "@Components/forms/helpers/FieldValidator";
import { formStateChangeHandler } from "@Components/forms/helpers/handlers";
import { Button } from "@Components/ui";
import { IloginFormSection } from "@Types/login-page";
import { isIdentifyKlaviyoUser } from "@Utils/klaviyo";
import { submitFormActionLogin } from "@wff/components/forms/helpers/commonFormSubmission";
import { dispatchCartFromCookie } from "@wff/hooks/useActionBasedOnStorage";
import { useLogin } from "@wff/hooks/useAuthHooks";
import { useCustomer } from "@wff/hooks/useCustomer";
import {
  FORM_TYPES,
  useFormFieldValidation,
} from "@wff/hooks/useFormFieldValidation";
import { useLocaleRouter } from "@wff/hooks/useLocaleRouter";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";

interface ILoginFormProps {
  onClickForgotPassword: () => void;
  loginFormSection?: IloginFormSection;
}

const LoginForm: FC<ILoginFormProps> = ({
  onClickForgotPassword,
  loginFormSection,
}) => {
  const loginEmailInputPlaceholder =
    loginFormSection?.loginEmailInputPlaceholder;
  const loginPasswordInputPlaceholder =
    loginFormSection?.loginPasswordInputPlaceholder;
  const forgotPasswordLinkText = loginFormSection?.forgotPasswordLinkText;
  const loginButtonText = loginFormSection?.loginButtonText;
  const loginErrorHeading = loginFormSection?.loginErrorHeading || "";
  const loginErrorMessage = loginFormSection?.loginErrorMessage || ""; // using the
  const EmailInputLabel = loginEmailInputPlaceholder || "";
  const PasswordInputLabel = loginPasswordInputPlaceholder || "";

  const dispatch = useAppDispatch();
  const { push } = useLocaleRouter();
  const { t } = useTranslation("common");
  const { loginState, setLoginState } = useLogin();
  const { getCustomerDetails } = useCustomer();
  const { locale, customerInfo } = useAppSelector((state) => state);
  const { email: loggedInUserEmail, firstName, lastName } = customerInfo;
  const currentLocale = locale;
  const router = useRouter();
  const redirectTo = router.query?.to?.toString();
  const errorCodeTranslation = {
    WF_CUSTOMER_004: loginErrorMessage,
  };

  const validationTranslation = {
    allFieldsBlank: t("customer.form_errors.all_fields_blank"),
    firstNameBlank: t("customer.form_errors.invalid_first_name"),
    lastNameBlank: t("customer.form_errors.invalid_last_name"),
    emailBlank: t("customer.form_errors.blank_email"),
    emailNeedsContentAfterAtSymbol: t(
      "customer.form_errors.format_ends_with_at"
    ),
    passwordBlank: t("customer.form_errors.blank_password_field"),
    confirmPasswordBlank: t("customer.form_errors.blank_password_field"),
    passwordsDoNotMatch: t("customer.form_errors.password_mismatch"),
  };

  /**
   * This method triggers the Klaviyo identify event upon the successfull user login
   */
  const triggerKlaviyoUserIdentifyEvent = () => {
    const paramObj = {
      $email: loggedInUserEmail,
      $first_name: firstName,
      $last_name: lastName,
    };
    isIdentifyKlaviyoUser(paramObj);
  };

  const {
    touched,
    setTouched,
    setFormError,
    formErrorMessages,
    setFormActionIsActive,
  } = useFormFieldValidation({
    type: FORM_TYPES.LOGIN,
    touched: true, // this form does not require touching for validation
    formState: loginState,
    languageCode: currentLocale?.languageCode,
    errorCodeTranslation,
    validationTranslation,
    formAction: async () => {
      submitFormActionLogin({
        dispatch,
        payload: loginState,
        setFormError,
        errorCodeTranslation,
        actionOnFormSuccess: () => {
          dispatchCartFromCookie({ dispatch });
          triggerKlaviyoUserIdentifyEvent();
          getCustomerDetails(); // TODO from Jared: if /account gets updated in the near future to make this call (there could be a future refactor that does this) on mount, we may be able to delete this getCustomerDetails() call from this here
          push(redirectTo ?? "/account");
        },
      });
    },
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    fieldStateKey: string
  ) => {
    formStateChangeHandler({
      key: fieldStateKey,
      value: e.target.value,
      formState: loginState,
      setTouched,
      formStateSetter: setLoginState,
    });
  };

  const handleOnClickSubmit = (
    e: SyntheticEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setFormActionIsActive(true);
  };

  return (
    <Form
      onSubmit={noop}
      testID="login_form"
      render={({}) => {
        return (
          <form
            className="form form--customer-login"
            onSubmit={noop}
            aria-label="checkout login form"
          >
            <FormMessage
              message={loginErrorHeading}
              type="error"
              errors={formErrorMessages}
            />
            <FormTextField
              aria-label="Email"
              name="email"
              data-testid="form_field-email"
              label={EmailInputLabel}
              validate={CTFieldValidator(
                [
                  required(t("customer.form_errors.blank_email")),
                  email(t("customer.form_errors.invalid_email")),
                ],
                "Email"
              )}
              outlined={true}
              value={loginState.email}
              onChange={(e) => {
                handleInputChange(e, "email");
              }}
            />
            <FormTextField
              aria-label="Password"
              name="password"
              data-testid="form_field-password"
              label={PasswordInputLabel}
              validate={CTFieldValidator(
                [
                  required(t("customer.form_errors.blank_password_field")),
                  currentLocale?.languageCode === "en-CA" ||
                  currentLocale?.languageCode === "fr-CA"
                    ? strongPasswordForCA(
                        t("customer.form_errors.blank_password_field")
                      )
                    : strongPassword(
                        t("customer.form_errors.blank_password_field")
                      ),
                ],
                "Password"
              )}
              type="password"
              outlined={true}
              value={loginState.password}
              onChange={(e) => {
                handleInputChange(e, "password");
              }}
            />
            <div className="form__link form__link--forgot-password">
              <a data-testid="forgot_password" onClick={onClickForgotPassword}>
                {forgotPasswordLinkText}
              </a>
            </div>
            <div className="form__actions">
              <Button
                data-testid="form_field-button_submit"
                className="action"
                onClick={handleOnClickSubmit}
                disabled={!touched}
                type="submit"
              >
                {loginButtonText}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default LoginForm;
